import axios, { AxiosInstance, AxiosRequestConfig, AxiosError, InternalAxiosRequestConfig } from 'axios';

declare global {
  interface Window {
    refreshTokenTimeout?: NodeJS.Timeout;
  }
}

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
  skipAuthRefresh?: boolean;
  [key: string]: unknown;
}

interface BaseModel {
  id: string;
  createdAt: string;
  updatedAt: string;
}

export interface User extends BaseModel {
  email: string;
  name?: string;
  role: string;
  avatar?: string;
  isActive?: boolean;
}

/** Objetos de asignación flexibles */
export interface AssignedUser {
  id: string;
  name?: string;
  email?: string;
}

/** Lead interface matching the frontend's Lead type */
export interface Lead extends BaseModel {
  name?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  phone?: string;
  status: string;
  source?: string;
  notes?: string;
  assignedTo?: string | AssignedUser;
  lastContacted?: string;
  subject?: string;
  from?: string | { emailAddress: { address: string; name: string } };
  receivedDate?: string;
  rawEmail?: Record<string, unknown>;
  interestedInModel?: string;
}

export interface Task extends BaseModel {
  title: string;
  description?: string;
  dueDate?: string;
  status?: 'pending' | 'in_progress' | 'completed' | 'cancelled' | string;
  priority?: 'low' | 'medium' | 'high' | string;
  assignedTo?: string | AssignedUser;
  leadId?: string;
}

/** Activity flexible con variaciones reales del backend */
export interface Activity extends BaseModel {
  type?: 'call' | 'email' | 'meeting' | 'note' | 'other' | string;
  title?: string;
  description?: string;
  status?: string;
  /** algunos endpoints usan dueDate, otros date */
  dueDate?: string;
  date?: string;

  leadId?: string;
  lead?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    assignedTo?: string | AssignedUser;
    status?: string;
  } | null;

  /** puede venir id plano o el objeto del usuario */
  assignedTo?: string | AssignedUser;
  assignedToId?: string;

  completedAt?: string;

  /** puede venir solo id, o el objeto columna */
  columnId?: string;
  column?: {
    id: string;
    name: string;
    color?: string;
    order?: number;
  };
}

export interface Meeting extends BaseModel {
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  location?: string;
  status?: string;
  leadId?: string;
  participants?: string[] | AssignedUser[];
}

export interface Notification extends BaseModel {
  type: string;
  message: string;
  read: boolean;
  userId: string;
  link?: string;
  metadata?: Record<string, unknown>;
}

export interface ActivityNotification extends BaseModel {
  activityId: string;
  minutesBefore: number;
  sent: boolean;
  sentAt?: string;
}

/** Columnas: los campos extra son opcionales para no chocar con DB real */
export interface ActivityColumn extends BaseModel {
  name: string;
  color?: string;
  order?: number;
  statuses?: string[];
  isDefault?: boolean;
}

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:4000';

export interface ApiResponse<T = unknown> {
  data: T;
  message?: string;
  success?: boolean;
}
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export class ApiClient {
  public client: AxiosInstance;
  private isRefreshing = false;
  private logger = {
    debug: (message: string, ...args: unknown[]) => console.debug(`[ApiClient] ${message}`, ...args),
    info: (message: string, ...args: unknown[]) => console.info(`[ApiClient] ${message}`, ...args),
    warn: (message: string, ...args: unknown[]) => console.warn(`[ApiClient] ${message}`, ...args),
    error: (message: string, ...args: unknown[]) => console.error(`[ApiClient] ${message}`, ...args)
  };
  private failedQueue: Array<{ resolve: (token: string) => void; reject: (error: Error) => void; }> = [];

  // Define public routes that don't require auth
  private isPublicRoute = (url: string | undefined): boolean => {
    if (!url) return false;
    const publicRoutes = [
      '/commercial-registration/validate-token',
      '/register/commercial',
      '/api/commercial-registration/validate-token'
    ];
    return publicRoutes.some(route => url.includes(route));
  };

  private processQueue = (error: unknown, token: string | null = null) => {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        const errorObj = error instanceof Error ? error : new Error(String(error));
        reject(errorObj);
      } else if (token) {
        resolve(token);
      }
    });
    this.failedQueue = [];
  };

  public setAuthToken = (token: string | null) => {
    if (token) {
      this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      if (typeof window !== 'undefined') localStorage.setItem('token', token);
    } else {
      delete this.client.defaults.headers.common['Authorization'];
      if (typeof window !== 'undefined') localStorage.removeItem('token');
    }
  };
  public setRefreshToken = (token: string | null) => {
    if (typeof window === 'undefined') return;
    if (token) localStorage.setItem('refreshToken', token);
    else localStorage.removeItem('refreshToken');
  };
  public getAuthToken = () => (typeof window === 'undefined' ? null : localStorage.getItem('token'));
  public getRefreshToken = () => (typeof window === 'undefined' ? null : localStorage.getItem('refreshToken'));
  public clearAuth = () => { 
    this.setAuthToken(null); 
    this.setRefreshToken(null);
    // Limpiar timeout si existe
    if (typeof window !== 'undefined' && window.refreshTokenTimeout) {
      clearTimeout(window.refreshTokenTimeout);
    }
  };

  private refreshToken = async (): Promise<string> => {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.logger.warn('No refresh token available');
      throw new Error('No refresh token available');
    }

    try {
      this.logger.debug('Attempting to refresh access token');
      const config: CustomAxiosRequestConfig = {
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        withCredentials: true,
        skipAuthRefresh: true,
        _retry: true, // Marcar como reintento para evitar bucles
      };

      const response = await axios.post<unknown>(
        `${API_BASE_URL}/auth/refresh-token`, 
        { refreshToken }, 
        config
      );

      const responseData = this.unwrap<{ 
        access_token: string; 
        refresh_token?: string; 
        expires_in?: number;
      }>(response.data);

      if (!responseData?.access_token) {
        throw new Error('No access token in refresh response');
      }

      const { access_token, refresh_token, expires_in } = responseData;
      
      // Actualizar tokens
      this.setAuthToken(access_token);
      if (refresh_token) {
        this.setRefreshToken(refresh_token);
      }

      // Programar próximo refresh
      if (expires_in && typeof window !== 'undefined') {
        // Refrescar 1 minuto antes de que expire (mínimo 5 segundos)
        const refreshTime = Math.max(5000, (expires_in - 60) * 1000);
        
        if (window.refreshTokenTimeout) {
          clearTimeout(window.refreshTokenTimeout);
        }
        
        window.refreshTokenTimeout = setTimeout(() => {
          this.logger.debug('Auto-refreshing token before expiration');
          this.refreshToken().catch((error) => {
            this.logger.error('Auto-refresh failed:', error);
            this.clearAuth();
          });
        }, refreshTime);
      }

      this.logger.debug('Token refreshed successfully');
      return access_token;
    } catch (error) {
      this.logger.error('Failed to refresh token:', error);
      this.clearAuth();
      
      throw error;
    }
  };

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      withCredentials: true,
    });

    this.client.interceptors.request.use(
      (config) => {
        console.log('[API] Request interceptor - URL:', config.url);
        let token = this.getAuthToken();
        if (!token && typeof window !== 'undefined') {
          token = localStorage.getItem('token') || undefined || null;
          if (token) this.setAuthToken(token);
        }
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
          console.log('[API] Added Authorization header with token');
        } else {
          console.warn('[API] No auth token available for request to:', config.url);
        }
        return config;
      },
      (error) => {
        console.error('[API] Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    this.client.interceptors.response.use(
      (response) => {
        console.log(`[API] Response received from ${response.config.url}:`, response.status);
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { 
          _retry?: boolean;
          skipAuthRefresh?: boolean;
        };

        console.error('[API] Response error:', {
          url: originalRequest?.url,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          headers: error.response?.headers,
        });

        // Skip handling for public routes or when explicitly skipped
        if (this.isPublicRoute(originalRequest?.url) || originalRequest?.skipAuthRefresh) {
          console.log('[API] Skipping auth refresh for public/skipped route:', originalRequest?.url);
          return Promise.reject(error);
        }

        // Si no es un error 401 o ya se reintentó, rechazar
        if (!originalRequest || error.response?.status !== 401 || originalRequest._retry) {
          // Solo limpiar la autenticación y redirigir si es un error 401 real
          if (error.response?.status === 401 && typeof window !== 'undefined') {
            // Verificar si ya estamos en la página de login para evitar bucles
            if (!window.location.pathname.includes('login') && !window.location.pathname.includes('auth')) {
              this.logger.warn('Authentication failed, clearing auth and redirecting to login');
              this.clearAuth();
              localStorage.setItem('postLoginRedirect', window.location.pathname + window.location.search);
              window.location.href = '/login';
            }
          }
          return Promise.reject(error);
        }

        if (this.isRefreshing) {
          return new Promise((resolve, reject) => {
            this.failedQueue.push({
              resolve: (token: string) => {
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(this.client(originalRequest));
              },
              reject,
            });
          });
        }

        originalRequest._retry = true;
        this.isRefreshing = true;

        try {
          const newToken = await this.refreshToken();
          this.processQueue(null, newToken);
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return this.client(originalRequest);
        } catch (refreshError) {
          // Limpiar autenticación y redirigir a login
          this.clearAuth();
          this.processQueue(refreshError instanceof Error ? refreshError : new Error(String(refreshError)), null);
          
          // Verificar si estamos en el cliente antes de usar window
          if (typeof window !== 'undefined') {
            // Solo redirigir si no estamos ya en la página de login o auth
            if (!window.location.pathname.includes('login') && !window.location.pathname.includes('auth')) {
              // Guardar la URL actual para redirigir después del login
              const redirectUrl = window.location.pathname + window.location.search;
              if (redirectUrl !== '/') {  // No guardar la raíz como redirección
                localStorage.setItem('postLoginRedirect', redirectUrl);
              }
              window.location.href = '/login';
            }
          }
          return Promise.reject(refreshError);
        } finally {
          this.isRefreshing = false;
        }
      }
    );
  }

  private hasDataEnvelope<T>(body: unknown): body is { data: T } {
    return typeof body === 'object' && body !== null && 'data' in body;
  }
  private unwrap<T>(body: unknown): T {
    return this.hasDataEnvelope<T>(body) ? (body as { data: T }).data : (body as T);
  }

  async get<T>(url: string, config?: CustomAxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.get<unknown>(url, config);
      return this.unwrap<T>(response.data);
    } catch (error) {
      console.error('GET request failed:', error);
      throw error;
    }
  }
  async post<T, D = Record<string, unknown>>(url: string, data?: D, config?: CustomAxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.post<unknown>(url, data, { ...config, validateStatus: (s) => s < 500 });
      if (response.status >= 400) {
        const axiosLikeError = Object.assign(new Error(`Request failed with status code ${response.status}`), {
          response: { status: response.status, statusText: '', data: response.data, headers: response.headers, config: {} },
        });
        throw axiosLikeError;
      }
      return this.unwrap<T>(response.data);
    } catch (error) {
      console.error('POST request failed:', { url, error });
      throw error;
    }
  }
  async put<T, D = Record<string, unknown>>(url: string, data?: D, config?: CustomAxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.put<unknown>(url, data, config);
      return this.unwrap<T>(response.data);
    } catch (error) {
      console.error('PUT request failed:', error);
      throw error;
    }
  }
  async delete<T>(url: string, config?: CustomAxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.delete<unknown>(url, config);
      return this.unwrap<T>(response.data);
    } catch (error) {
      console.error('DELETE request failed:', error);
      throw error;
    }
  }
  async patch<T, D = Record<string, unknown>>(url: string, data?: D, config?: CustomAxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.patch<unknown>(url, data, config);
      return this.unwrap<T>(response.data);
    } catch (error) {
      console.error('PATCH request failed:', error);
      throw error;
    }
  }

  // --------- LEADS ----------
  leads = {
    getAll: (params?: { page?: number; limit?: number; status?: Lead['status']; search?: string; }) =>
      this.get<PaginatedResponse<Lead>>('/leads', { params }),
    getById: (id: string) => this.get<Lead>(`/leads/${id}`),
    create: (data: Omit<Lead, 'id' | 'createdAt' | 'updatedAt'>) =>
      this.post<Lead, Omit<Lead, 'id' | 'createdAt' | 'updatedAt'>>('/leads', data),
    update: (id: string, data: Partial<Omit<Lead, 'id' | 'createdAt' | 'updatedAt'>>) =>
      this.patch<Lead, Partial<Omit<Lead, 'id' | 'createdAt' | 'updatedAt'>>>(`/leads/${id}`, data),
    delete: (id: string) => this.delete<{ success: boolean }>(`/leads/${id}`),
    getByUser: () => this.get<Lead[]>('/leads/by-user'),
    addNote: (leadId: string, note: string) =>
      this.post<{ success: boolean }, { note: string }>(`/leads/${leadId}/notes`, { note }),
  };

  // --------- TASKS ----------
  tasks = {
    getSummary: () => this.get<{ total: number; items: Task[] } | { items: Task[] } | Task[]>('/tasks/summary'),
    getCalendarData: async () => {
      try {
        return await this.get<Task[]>('/tasks/calendar');
      } catch (e: unknown) {
        if (axios.isAxiosError(e) && e.response?.status === 404) {
          return await this.tasks.getSummary();
        }
        throw e;
      }
    },
    update: (id: string, data: Partial<Omit<Task, 'id' | 'createdAt' | 'updatedAt'>>) =>
      this.patch<Task, Partial<Omit<Task, 'id' | 'createdAt' | 'updatedAt'>>>(`/tasks/${id}`, data),
  };

  // --------- ACTIVITIES ----------
  activities = {
    getSummary: async (): Promise<{ total: number; items: Activity[] }> => {
      try {
        const response = await this.client.get('/activities/summary');
        // Handle both response formats: direct data or nested in data property
        if (response.data && typeof response.data === 'object') {
          if ('data' in response.data) {
            return response.data.data;
          }
          if ('items' in response.data && 'total' in response.data) {
            return response.data as { total: number; items: Activity[] };
          }
        }
        // Fallback to empty result
        return { total: 0, items: [] };
      } catch (error) {
        console.error('Failed to fetch activities summary:', error);
        return { total: 0, items: [] };
      }
    },
    getCalendarData: async () => {
      try {
        return await this.get<Activity[]>('/activities/calendar');
      } catch (e: unknown) {
        if (axios.isAxiosError(e) && e.response?.status === 404) {
          return await this.activities.getSummary();
        }
        throw e;
      }
    },
    create: (data: Omit<Activity, 'id' | 'createdAt' | 'updatedAt'>) =>
      this.post<Activity, Omit<Activity, 'id' | 'createdAt' | 'updatedAt'>>('/activities', data),
    updateStatus: (id: string, status: string) =>
      this.patch<Activity, { status: string }>(`/activities/${id}/status`, { status }),
    update: (id: string, data: Partial<Activity>) =>
      this.patch<Activity, Partial<Activity>>(`/activities/${id}`, data),
    getByLead: (leadId: string) => this.get<Activity[]>(`/leads/${leadId}/activities`),
    moveToColumn: (activityId: string, columnId: string) =>
      this.patch<Activity, { columnId: string }>(`/activities/${activityId}/move-to-column`, { columnId }),

    // --- Activity notifications ---
    getActivityNotifications: (activityId: string) => {
      return this.get<ActivityNotification[]>(`/activity-notifications/activity/${activityId}`);
    },
    createActivityNotification: (activityId: string, minutesBefore: number) =>
      this.post<ActivityNotification, { activityId: string; minutesBefore: number }>(
        '/activity-notifications',
        { activityId, minutesBefore }
      ),
    deleteActivityNotification: (notificationId: string) =>
      this.delete<{ success: boolean }>(`/activity-notifications/${notificationId}`),
    deleteAllActivityNotifications: async (activityId: string) => {
      try {
        return await this.delete<{ success: boolean }>(`/activity-notifications/activity/${activityId}/notifications`);
      } catch (e: unknown) {
        if (axios.isAxiosError(e) && e.response?.status === 404) {
          return await this.delete<{ success: boolean }>(`/activities/${activityId}/notifications`);
        }
        throw e;
      }
    },
  };

  // --------- MEETINGS ----------
  meetings = {
    getSummary: () => this.get<{ total?: number; items: Meeting[] } | Meeting[]>('/meetings/summary'),
    getCalendarData: async () => {
      try {
        return await this.get<Meeting[]>('/meetings/calendar');
      } catch (e: unknown) {
        if (axios.isAxiosError(e) && e.response?.status === 404) {
          const sum = await this.meetings.getSummary();
          if (Array.isArray(sum)) return sum;
          if ('items' in (sum as { items: Meeting[] })) return (sum as { items: Meeting[] }).items;
        }
        throw e;
      }
    },
    update: (id: string, data: Partial<Omit<Meeting, 'id' | 'createdAt' | 'updatedAt'>>) =>
      this.patch<Meeting, Partial<Omit<Meeting, 'id' | 'createdAt' | 'updatedAt'>>>(`/meetings/${id}`, data),
  };

  // --------- USERS ----------
  users = {
    getProfile: () => this.get<User>('/users/me'),
    getTeam: () => this.get<User[]>('/users/team'),
    getTeamStats: () => this.get<{ totalLeads: number; activeLeads: number; completedTasks: number; pendingTasks: number; upcomingMeetings: number; recentActivities: Activity[]; }>('/users/team-stats'),
  };

  commercialInvitations = {
    generateLink: () => this.get<{ link: string }>('/commercial-invitations/generate-link'),
    validateToken: (token: string) => this.get<{ 
      valid: boolean; 
      email?: string;
      name?: string;
      salesManagerId?: string;
      message?: string;
    }>(`/commercial-registration/validate-token?token=${encodeURIComponent(token)}`, {
      skipAuthRefresh: true,
      validateStatus: (status) => status < 500 // Consider all status codes < 500 as not errors
    }),
  };

    // --------- NOTIFICATIONS ----------
  notifications = {
    getSummary: async (): Promise<{ total: number; items: Notification[] }> => {
      try {
        console.log('Fetching notifications summary...');
        const response = await this.client.get('/notifications/summary', {
          validateStatus: status => status < 500 // Don't throw for 4xx errors
        });
        
        console.log('Summary response:', {
          status: response.status,
          data: response.data
        });

        // If endpoint returns 404, return empty results
        if (response.status === 404) {
          console.warn('Notifications summary endpoint not found (404)');
          return { total: 0, items: [] };
        }

        // Handle successful response
        if (response.status === 200) {
          const data = response.data?.data || response.data;
          if (data && typeof data === 'object') {
            return {
              total: data.total || 0,
              items: Array.isArray(data.items) ? data.items : []
            };
          }
        }

        console.warn('Unexpected response format for notifications summary');
        return { total: 0, items: [] };
      } catch (error) {
        console.error('Error in getSummary:', {
          error,
          response: (error as AxiosError).response?.data,
          status: (error as AxiosError).response?.status
        });
        return { total: 0, items: [] };
      }
    },

    getUnreadCount: async (): Promise<number> => {
      try {
        console.log('Fetching unread count...');
        const response = await this.client.get('/notifications/unread-count', {
          validateStatus: status => status < 500 // Don't throw for 4xx errors
        });

        console.log('Unread count response:', {
          status: response.status,
          data: response.data
        });

        // If endpoint returns 404, return 0
        if (response.status === 404) {
          console.warn('Unread count endpoint not found (404)');
          return 0;
        }

        // Handle successful response
        if (response.status === 200) {
          const data = response.data?.data || response.data;
          
          // Handle numeric response directly
          if (typeof data === 'number') {
            return Math.max(0, data);
          }
          
          // Handle object response
          if (data && typeof data === 'object') {
            if ('unreadCount' in data) return Math.max(0, Number(data.unreadCount) || 0);
            if ('count' in data) return Math.max(0, Number(data.count) || 0);
            if ('total' in data) return Math.max(0, Number(data.total) || 0);
          }
        }

        console.warn('Unexpected response format for unread count');
        return 0;
      } catch (error) {
        console.error('Error in getUnreadCount:', {
          error,
          response: (error as AxiosError).response?.data,
          status: (error as AxiosError).response?.status
        });
        return 0;
      }
    },

    markAsRead: (id: string): Promise<Notification> => {
      return this.patch<Notification, { read: boolean }>(`/notifications/${id}/read`, { read: true });
    },

    markAsUnread: (id: string): Promise<Notification> => {
      return this.patch<Notification, { read: boolean }>(`/notifications/${id}/read`, { read: false });
    },

    markAllAsRead: (): Promise<{ count: number }> => {
      return this.patch<{ count: number }, Record<string, never>>('/notifications/mark-all-read', {});
    },
  };

  // --------- ACTIVITY COLUMNS ----------
  activityColumns = {
    getAll: () => this.get<ActivityColumn[]>('/activity-columns'),
    create: (data: Partial<ActivityColumn>) =>
      this.post<ActivityColumn, Partial<ActivityColumn>>('/activity-columns', data),
    update: (id: string, data: Partial<ActivityColumn>) =>
      this.patch<ActivityColumn, Partial<ActivityColumn>>(`/activity-columns/${id}`, data),
    delete: (id: string) => this.delete<{ success: boolean }>(`/activity-columns/${id}`),
  };

  // --------- KANBAN (si lo sigues usando) ----------
  activitiesKanban = {
    findByColumn: (columnId: string) => this.get<Activity[]>(`/activities/kanban/${columnId}`),
    moveToColumn: (activityId: string, columnId: string) =>
      this.patch<Activity, { columnId: string }>(`/activities/kanban/${activityId}/move`, { columnId }),
  };
}

export const api = new ApiClient();
export default api;